import type { ServerNotification } from "../types";

/**
 * Pure, in-place patches for the notification dropdown's infinite-query
 * cache. The `system:notification` socket push carries everything the bell
 * needs (the row itself on `created`, the id on `read` / `dismissed`, the
 * recipient's new unread count when the server bothered to compute it), so
 * a push must NEVER cost a GET /core/notifications — with ~55 recipients per
 * event and three queries sharing the old `["notifications"]` prefix, the
 * invalidate-on-push design produced 475k list GETs a day.
 *
 * Kept free of React / axios imports so it runs under the repo's node-env
 * vitest as-is.
 */

export interface NotificationListPage {
  status?: string;
  data: ServerNotification[];
  nextCursor: string | null;
  hasMore: boolean;
  /** Scoped unread count — the badge reads `pages[0].unreadCount`. */
  unreadCount: number;
}

export interface NotificationListData {
  pages: NotificationListPage[];
  pageParams: unknown[];
}

/** The filter object that sits at `queryKey[1]` of every list query. */
export interface NotificationListFilter {
  unreadOnly: boolean;
  type: string | null;
  values: string[] | null;
}

/** Wire shape of the `created` push's `notification` (no user/read columns). */
export interface PushedNotification {
  id: number;
  type: string;
  payload: ServerNotification["payload"];
  createdAt: string;
}

export type NotificationPush =
  | {
      v?: number;
      action: "created";
      notification: PushedNotification;
      /** `null` when the server skipped the per-recipient count — increment. */
      unreadCount: number | null;
    }
  | {
      v?: number;
      action: "read";
      /** `null` = read-all. */
      notificationId: number | null;
      unreadCount: number | null;
    }
  | { v?: number; action: "dismissed"; notificationId: number | null }
  | { v?: number; action: "cleared"; notificationId?: number | null };

/** Does a pushed row belong in a list filtered this way? Mirrors the server's list filter. */
export function listFilterMatches(
  filter: NotificationListFilter | undefined,
  n: Pick<ServerNotification, "type" | "payload">,
): boolean {
  if (!filter) return true;
  if (filter.type && filter.type !== n.type) return false;
  if (filter.values && filter.values.length > 0) {
    const from = n.payload?.fromValue;
    const to = n.payload?.toValue;
    if (!filter.values.some((v) => v === from || v === to)) return false;
  }
  return true;
}

/** Fill the columns the push omits so the row renders like a fetched one. */
export function toServerNotification(
  n: PushedNotification,
  ids: { userId: number; tenantId: number },
): ServerNotification {
  return {
    id: n.id,
    userId: ids.userId,
    tenantId: ids.tenantId,
    type: n.type as ServerNotification["type"],
    payload: n.payload,
    readAt: null,
    dismissedAt: null,
    // Server default is 30 days; only used for display, never enforced here.
    expiresAt: new Date(
      new Date(n.createdAt).getTime() + 30 * 24 * 60 * 60 * 1000,
    ).toISOString(),
    createdAt: n.createdAt,
  };
}

function withUnread(
  data: NotificationListData,
  unreadCount: number,
): NotificationListData {
  const safe = Math.max(0, unreadCount);
  return {
    ...data,
    pages: data.pages.map((p, i) =>
      i === 0 ? { ...p, unreadCount: safe } : p,
    ),
  };
}

export function currentUnread(data: NotificationListData): number {
  return data.pages[0]?.unreadCount ?? 0;
}

function hasId(data: NotificationListData, id: number): boolean {
  return data.pages.some((p) => p.data.some((n) => n.id === id));
}

/**
 * `created`: prepend the row to page 0 when the list's filter admits it and
 * bump the badge either way (the count is scope-wide, not per filter).
 * Idempotent on the id — a second tab's echo or an overlapping refetch must
 * not double-list it.
 */
export function prependNotification(
  data: NotificationListData | undefined,
  n: ServerNotification,
  filter: NotificationListFilter | undefined,
  unreadCount: number | null,
): NotificationListData | undefined {
  if (!data || data.pages.length === 0) return data;
  const already = hasId(data, n.id);
  const nextUnread =
    unreadCount != null
      ? unreadCount
      : already
        ? currentUnread(data)
        : currentUnread(data) + 1;
  if (already || !listFilterMatches(filter, n)) {
    return withUnread(data, nextUnread);
  }
  const [first, ...rest] = data.pages;
  return withUnread(
    { ...data, pages: [{ ...first, data: [n, ...first.data] }, ...rest] },
    nextUnread,
  );
}

/**
 * `read`: stamp `readAt` on one row (or every row for read-all). On an
 * unread-only list the row leaves instead. A `null` unreadCount is derived:
 * minus one per row that was actually unread, zero for read-all.
 */
export function markNotificationsRead(
  data: NotificationListData | undefined,
  ids: number[] | null,
  unreadCount: number | null,
  filter: NotificationListFilter | undefined,
  readAt: string = new Date().toISOString(),
): NotificationListData | undefined {
  if (!data || data.pages.length === 0) return data;
  const all = ids == null;
  const wanted = new Set(ids ?? []);
  let flipped = 0;
  const pages = data.pages.map((p) => ({
    ...p,
    data: p.data
      .map((n) => {
        if (!(all || wanted.has(n.id)) || n.readAt) return n;
        flipped += 1;
        return { ...n, readAt };
      })
      .filter((n) => !(filter?.unreadOnly && n.readAt)),
  }));
  const nextUnread =
    unreadCount != null
      ? unreadCount
      : all
        ? 0
        : currentUnread(data) - flipped;
  return withUnread({ ...data, pages }, nextUnread);
}

/** `dismissed`: drop the row; an unread row leaving also lowers the badge. */
export function removeNotification(
  data: NotificationListData | undefined,
  id: number,
  unreadCount: number | null = null,
): NotificationListData | undefined {
  if (!data || data.pages.length === 0) return data;
  let wasUnread = false;
  const pages = data.pages.map((p) => ({
    ...p,
    data: p.data.filter((n) => {
      if (n.id !== id) return true;
      if (!n.readAt) wasUnread = true;
      return false;
    }),
  }));
  const nextUnread =
    unreadCount != null
      ? unreadCount
      : currentUnread(data) - (wasUnread ? 1 : 0);
  return withUnread({ ...data, pages }, nextUnread);
}

/** `cleared`: every row gone, badge to zero, pagination reset. */
export function clearNotifications(
  data: NotificationListData | undefined,
): NotificationListData | undefined {
  if (!data || data.pages.length === 0) return data;
  const first = data.pages[0];
  return {
    pages: [{ ...first, data: [], nextCursor: null, hasMore: false, unreadCount: 0 }],
    pageParams: data.pageParams.slice(0, 1),
  };
}
